import type { MenuItem } from '@/config/navigation';
import type { User, Role, UserRole } from '@/types/user';

export const shouldShowMenuItem = (
  menuItem: MenuItem, 
  user: User | null | undefined, 
  isAuthenticated: boolean
): boolean => {
  // If publicOnly is true, only show when not authenticated
  if (menuItem.publicOnly && isAuthenticated) {
    return false;
  }

  // If authRequired is true, only show when authenticated
  if (menuItem.authRequired && !isAuthenticated) {
    return false;
  }

  // If roles are specified, check if user's role is included
  if (menuItem.roles && user?.role) {
    const userRole = getUserRole(user.role);
    return menuItem.roles.includes(userRole.slug as UserRole);
  }

  // If no restrictions, show to all
  return true;
};

export const getUserRole = (role: Role|UserRole|number|null): Role => {
  const roleIds: Record<UserRole, number> = {
    'unsubscribed': 1,
    'free-learner': 1,
    'trail-learner': 2,
    'plus-learner': 3,
    'super-admin': 4,
    'pending-plus-learner': 5,
    'visitor': 1,    
    'trial': 2,
    'plus': 3,
  };

  const roles: Record<number, Role> = {
    1: { id: 1, name: 'Unsubscribed', slug: 'unsubscribed' },
    2: { id: 2, name: 'Free Learner', slug: 'free-learner' },
    3: { id: 3, name: 'Plus Learner', slug: 'plus-learner' },
    4: { id: 4, name: 'Super Admin', slug: 'super-admin' },
    5: { id: 5, name: 'Pending Plus Learner', slug: 'pending-plus-learner' },
  };

  if (typeof role === 'number') {
    return roles[role || 1];
  } else if (typeof role === 'string') {
    const roleId = roleIds[role as UserRole] || null;
    const getRole = Object.values(roles).find(r => r?.slug === role || r?.name === role || r?.role_name === role);
    return getRole || (roleId ? roles[roleId] : roles[1]);
  } else if (role && typeof role === 'object') {
    const roleId = roleIds[role.slug as UserRole] || null;
    const roleStr = role?.slug || role?.name || role?.role_name || null;
    const getRole = Object.values(roles).find(r => r?.slug === roleStr || r?.name === roleStr || r?.role_name === roleStr);
    return role.id ? roles[role.id] : (roleId ? roles[roleId] : (roleStr && getRole ? getRole : roles[1]));
  } else {
    return roles[1]; // Default to 'unsubscribed'
  }
};

export const getUserBadge = (role: Role|UserRole|null): string|null => {
  const badges: Record<UserRole, string|null> = {
    'free-learner': null,
    'trail-learner': null,
    'plus-learner': 'PLUS',
    'super-admin': null,
    'pending-plus-learner': null,
    'visitor': null,
    'unsubscribed': null,
    'trial': null,
    'plus': 'PLUS'
  };
  
  const getRole = getUserRole(role);
  return getRole ? badges[getRole.slug as UserRole] : null;
};